/**
 * Ready-made scanner presets — one-click starting points for common day-trading
 * setups (e.g. a Ross-Cameron-style small-cap momentum "Gap & Go"). Each preset
 * is a full scanner: a named market universe + a trigger tree the user can then
 * tweak. Only fields that populate at the preset's universe scale are used
 * (bar-series indicators like RSI are left for watchlist-scoped scanners).
 */
import type { ScanGroupNode } from '../../types/strategyLab';

export interface ScannerPreset {
  id: string;
  name: string;
  description: string;
  universe: string;      // named universe value (see NAMED_UNIVERSES)
  root: ScanGroupNode;
}

export const SCANNER_PRESETS: ScannerPreset[] = [
  {
    id: 'gap_and_go',
    name: 'Gap & Go — small-cap momentum',
    description: 'Low-float small caps gapping up on high relative volume, holding above VWAP (Ross-Cameron style).',
    universe: 'most_active',
    root: {
      type: 'group',
      logic: 'and',
      children: [
        { type: 'condition', field: 'price', op: 'between', value: 1, value2: 20 },
        { type: 'condition', field: 'change_percent', op: 'gte', value: 10 },
        { type: 'condition', field: 'relative_volume', op: 'gte', value: 5 },
        { type: 'condition', field: 'float_shares', op: 'lt', value: 20_000_000 },
        { type: 'condition', field: 'dollar_volume', op: 'gte', value: 1_000_000 },
        { type: 'condition', field: 'price_vs_vwap', op: 'gt', value: 0 },
      ],
    },
  },
  {
    id: 'high_rvol_breakout',
    name: 'High-RVOL breakout',
    description: 'Any liquid name surging on unusual volume and pressing the session high.',
    universe: 'all',
    root: {
      type: 'group',
      logic: 'and',
      children: [
        { type: 'condition', field: 'price', op: 'gte', value: 2 },
        { type: 'condition', field: 'relative_volume', op: 'gte', value: 3 },
        { type: 'condition', field: 'change_percent', op: 'gte', value: 5 },
        { type: 'condition', field: 'price_vs_hod', op: 'gte', value: -1 },
        { type: 'condition', field: 'price_vs_vwap', op: 'gt', value: 0 },
      ],
    },
  },
  {
    id: 'gap_down_fade',
    name: 'Gap-down fade (short)',
    description: 'Weak names gapping down below VWAP on volume — short-side candidates.',
    universe: 'most_active',
    root: {
      type: 'group',
      logic: 'and',
      children: [
        { type: 'condition', field: 'price', op: 'gte', value: 1 },
        { type: 'condition', field: 'change_percent', op: 'lte', value: -8 },
        { type: 'condition', field: 'relative_volume', op: 'gte', value: 3 },
        { type: 'condition', field: 'price_vs_vwap', op: 'lt', value: 0 },
      ],
    },
  },
];
